<template>
  <div
    class="chat-item"
    :class="{
      'chat-item--continuation': continuation,
      'chat-item--mentions-me': mentionsMe,
      'chat-item--system': isFromSystem,
      'chat-item--done': isTask && message.status === ChatTaskStatus.Done,
    }"
  >
    <!-- Gutter: avatar, or the timestamp alone on a run continuation, plus the
         unread dot over whichever of the two is showing. -->
    <div class="chat-item__gutter">
      <template v-if="!continuation">
        <span v-if="isFromSystem" class="chat-item__avatar chat-item__avatar--system">
          <fs-tooltip text="Resourced">
            <span class="chat-item__system-avatar" v-html="resourcedLogo" />
          </fs-tooltip>
        </span>
        <button
          v-else-if="message.author"
          type="button"
          class="chat-item__avatar"
          :class="{ 'chat-item__avatar--static': isFromMe }"
          :title="isFromMe ? message.author.name : `Tag ${message.author.name}`"
          @click="!isFromMe && emit('tag-user', message.author.id)"
        >
          <user-avatar :name="message.author.name" :url="message.author.photo" size="small" />
        </button>
      </template>
      <!-- The hover timestamp gives way to the dot: unread is the more urgent
           of the two, and they share the gutter's top-right corner. -->
      <span v-else-if="!isUnread" class="chat-item__gutter-time">{{ shortTime }}</span>

      <!-- On the gutter, not the avatar: a system row has no author button and a
           run continuation has no avatar, and both can still be unread. -->
      <span v-if="isUnread" class="chat-item__unread-dot" />
    </div>

    <!-- Row-level, not part of the head: a run continuation has no head, and
         its messages are just as editable as the one that opened the run. -->
    <button
      v-if="isFromMe && !editing"
      type="button"
      class="chat-item__edit-btn"
      title="Edit"
      @click="startEdit"
      v-html="editIcon"
    />

    <div class="chat-item__content">
      <div v-if="!continuation" class="chat-item__head">
        <span class="chat-item__author">{{ authorName }}</span>
        <span class="chat-item__time" :title="fullTime">{{ shortTime }}</span>
        <button v-if="recipientHint" type="button" class="chat-item__hint" title="Tag these people" @click="tagAll">
          {{ recipientHint }}
        </button>
      </div>

      <!-- ── Task card ─────────────────────────────────────────────────── -->
      <div v-if="isTask" class="chat-item__task" :class="{ 'chat-item__task--done': isDone }">
        <div class="chat-item__task-head">
          <span ref="statusPillRef" class="chat-item__status-wrap">
            <button
              type="button"
              class="chat-item__status"
              :class="`chat-item__status--${statusKey}`"
              @click="statusMenuOpen = !statusMenuOpen"
            >
              <span class="chat-item__status-dot" />
              {{ statusLabel }}
              <span class="chat-item__status-caret" v-html="caretIcon" />
            </button>
          </span>

          <span
            v-if="!editing && message.deadline"
            class="chat-item__deadline"
            :class="`chat-item__deadline--${deadlineUrgency}`"
          >
            {{ isDone ? 'Closed' : 'Due' }} {{ deadlineLabel }}
          </span>

          <span v-if="editing" ref="editDeadlineButtonRef" class="chat-item__deadline-edit">
            <button
              v-if="!editDeadline"
              type="button"
              class="chat-item__deadline-btn"
              @click="editDeadlinePickerOpen = true"
            >
              Add deadline
            </button>
            <span v-else class="chat-item__deadline-chip">
              Due {{ editDeadlineLabel }}
              <button type="button" class="chat-item__remove" @click="editDeadline = null">
                <span v-html="crossIcon" />
              </button>
            </span>
          </span>

          <span class="chat-item__task-marker">Task</span>
        </div>

        <div v-if="!editing" class="chat-item__body">
          <span
            v-for="(segment, index) in bodySegments"
            :key="index"
            :class="{
              'chat-item__mention': segment.mentionUserId,
              'chat-item__mention--me': segment.mentionUserId && segment.mentionUserId === meId,
              'chat-item__role-mention': segment.mentionRoleId,
            }"
            @click="segment.mentionUserId && emit('tag-user', segment.mentionUserId)"
            >{{ segment.text }}</span
          >
        </div>
        <textarea-custom
          v-else
          v-model="editBody"
          class="chat-item__edit-input"
          :autosize="true"
          :min-height="50"
          :max-symbols="false"
        />

        <div v-if="editing" class="chat-item__edit-actions">
          <button type="button" class="chat-item__edit-cancel" @click="cancelEdit">Cancel</button>
          <button type="button" class="chat-item__edit-save" :disabled="!canSaveEdit" @click="saveEdit">Save</button>
        </div>

        <div v-if="message.contextUrl" class="chat-item__task-foot">
          <router-link class="chat-item__context chat-item__context--on-card" :to="message.contextUrl">
            <span class="chat-item__context-icon" v-html="linkIcon" />
            {{ message.contextLabel || 'Linked page' }}
          </router-link>
        </div>
      </div>

      <!-- ── Message ───────────────────────────────────────────────────── -->
      <template v-else>
        <div v-if="!editing" class="chat-item__body">
          <span
            v-for="(segment, index) in bodySegments"
            :key="index"
            :class="{
              'chat-item__mention': segment.mentionUserId,
              'chat-item__mention--me': segment.mentionUserId && segment.mentionUserId === meId,
              'chat-item__role-mention': segment.mentionRoleId,
            }"
            @click="segment.mentionUserId && emit('tag-user', segment.mentionUserId)"
            >{{ segment.text }}</span
          >
        </div>
        <textarea-custom
          v-else
          v-model="editBody"
          class="chat-item__edit-input"
          :autosize="true"
          :min-height="50"
          :max-symbols="false"
        />

        <div v-if="editing" class="chat-item__edit-actions">
          <button type="button" class="chat-item__edit-cancel" @click="cancelEdit">Cancel</button>
          <button type="button" class="chat-item__edit-save" :disabled="!canSaveEdit" @click="saveEdit">Save</button>
        </div>

        <router-link v-if="message.contextUrl" class="chat-item__context" :to="message.contextUrl">
          <span class="chat-item__context-icon" v-html="linkIcon" />
          {{ message.contextLabel || 'Linked page' }}
        </router-link>
      </template>

      <div v-if="isTask && message.statusChangedBy" class="chat-item__status-meta">{{ statusChangedText }}</div>
    </div>

    <!-- Status menu — one control in place of the old three-button row. -->
    <fs-popper
      v-if="isTask"
      :reference="statusPillRef"
      :show="statusMenuOpen"
      :close-on-outside-click="true"
      placement="bottom-start"
      :offset="[0, 6]"
      animation="none"
      no-styling
      @close="statusMenuOpen = false"
    >
      <ul class="chat-item__status-menu">
        <li v-for="option in statusOptions" :key="option.value">
          <button
            type="button"
            class="chat-item__status-option"
            :class="{ 'chat-item__status-option--current': message.status === option.value }"
            @click="pickStatus(option.value)"
          >
            <span class="chat-item__status-dot" :class="`chat-item__status-dot--${option.key}`" />
            {{ option.label }}
            <span v-if="message.status === option.value" class="chat-item__status-check">✓</span>
          </button>
        </li>
      </ul>
    </fs-popper>

    <fs-popper
      v-if="isTask && editing"
      :reference="editDeadlineButtonRef"
      :show="editDeadlinePickerOpen"
      :close-on-outside-click="true"
      placement="top-start"
      :offset="[0, 4]"
      animation="none"
      no-styling
      @close="editDeadlinePickerOpen = false"
    >
      <div class="chat-item__deadline-popper">
        <fs-date-picker inline :model-value="editDeadline ?? undefined" @pick="onEditDeadlinePicked" />
      </div>
    </fs-popper>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { differenceInCalendarDays, differenceInDays, format, formatDistanceToNow, isToday } from 'date-fns';
import { ChatItemKind, ChatTaskStatus, type ChatMessageDto } from '@/ApiClient';
import { useChat } from '@/store/chat';
import { splitBodyMentions } from '@lib/chat-mentions';
import { useRoles } from '@/store/roles';
import { dateToDateOnlyString, parseDateOnlyString, parseServerUtcDate } from '@lib/formatters';
import UserAvatar from '@components/user/user-avatar.vue';
import FsDatePicker from '@components/form-elements/fs-date-picker.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import TextareaCustom from '@components/form-elements/textarea-custom.vue';
import crossIcon from '@icons/other/ico-cross.svg';
import editIcon from '@icons/other/pencil-icon.svg';
import caretIcon from '@icons/other/caret-down.svg';
import linkIcon from '@icons/other2/link-chain.svg';
import resourcedLogo from '@assets/img/logo-icon.svg';

const {
  message,
  continuation = false,
  mentionsMe = false,
} = defineProps<{
  message: ChatMessageDto;
  /** Part of a run from the same author — drops the avatar and header line. */
  continuation?: boolean;
  /** The body names the current user; the row is tinted and never joins a run. */
  mentionsMe?: boolean;
}>();

const emit = defineEmits<{ 'tag-user': [userId: string] }>();

const meId = computed(() => useChat().meId);
const isTask = computed(() => message.kind === ChatItemKind.Task);
const isDone = computed(() => isTask.value && message.status === ChatTaskStatus.Done);
const isFromSystem = computed(() => message.author == null);
const isFromMe = computed(() => message.author?.id === meId.value);
const isUnread = computed(() => !isFromMe.value && message.recipients.some((r) => r.user.id === meId.value && !r.seen));

const authorName = computed(() => {
  if (isFromSystem.value) return 'Resourced';
  return isFromMe.value ? 'You' : (message.author?.name ?? '');
});

// Recipients other than the author (a self-assigned item has none). System
// messages have no author, so every recipient is "other".
const otherRecipients = computed(() => message.recipients.filter((r) => r.user.id !== message.author?.id));

const firstName = (name: string) => name.split(' ')[0];

// Who the item went to, in words. Omitted for the common case — a plain
// incoming message addressed only to you — where it would add nothing.
const recipientHint = computed<string | null>(() => {
  if (isFromMe.value) {
    if (otherRecipients.value.length === 0) return isTask.value ? 'self-assigned' : 'note to self';
    const names = otherRecipients.value.map((r) => firstName(r.user.name));
    if (names.length === 1) return `to ${names[0]}`;
    if (names.length === 2) return `to ${names[0]} & ${names[1]}`;
    return `to ${names[0]} +${names.length - 1}`;
  }
  if (message.recipients.length <= 1) return isTask.value ? 'for you' : null;
  return `to you +${message.recipients.length - 1}`;
});

// Everyone involved except yourself — tagged into the composer on hint click.
// System has no real user to tag, so it's skipped.
const taggableUserIds = computed(() => {
  const ids = new Set<string>();
  if (message.author && message.author.id !== meId.value) ids.add(message.author.id);
  for (const recipient of message.recipients) {
    if (recipient.user.id !== meId.value) ids.add(recipient.user.id);
  }
  return [...ids];
});

const tagAll = () => taggableUserIds.value.forEach((id) => emit('tag-user', id));

// Body split into text + "@mention" segments, so mentions render as chips.
const bodySegments = computed(() => {
  const segments = splitBodyMentions(
    message.body,
    message.recipients.map((recipient) => ({ id: recipient.user.id, name: recipient.user.name })),
    useRoles().loaded.map((role) => ({ id: role.id, name: role.name }))
  );
  const last = segments.at(-1);
  if (last && !last.mentionUserId && !last.mentionRoleId) last.text = last.text.replace(/\s+$/, '');
  return segments;
});

const toRelative = (raw: string): string =>
  formatDistanceToNow(parseServerUtcDate(raw), { addSuffix: true }).replace(/^about /, '');

// Clock time for today, weekday for the past week, then the date — the exact
// moment stays available in the title attribute.
const createdAt = computed(() => parseServerUtcDate(message.createdAt));

const shortTime = computed(() => {
  const date = createdAt.value;
  if (isToday(date)) return format(date, 'HH:mm');
  if (differenceInDays(new Date(), date) < 7) return format(date, 'EEE HH:mm');
  return format(date, 'd MMM HH:mm');
});

const fullTime = computed(() => format(createdAt.value, 'd MMMM yyyy, HH:mm'));

const statusWords: Record<ChatTaskStatus, string> = {
  [ChatTaskStatus.Open]: 'open',
  [ChatTaskStatus.Ongoing]: 'ongoing',
  [ChatTaskStatus.Done]: 'done',
};

// Discreet note of who last changed a task's status.
const statusChangedText = computed(() => {
  const changedBy = message.statusChangedBy;
  if (!changedBy) return '';
  const who = changedBy.id === meId.value ? 'You' : firstName(changedBy.name);
  const when = message.statusChangedAt ? ` · ${toRelative(message.statusChangedAt)}` : '';
  return `${who} set it to ${statusWords[message.status]}${when}`;
});

const statusOptions = [
  { value: ChatTaskStatus.Open, key: 'open', label: 'Open' },
  { value: ChatTaskStatus.Ongoing, key: 'ongoing', label: 'Ongoing' },
  { value: ChatTaskStatus.Done, key: 'done', label: 'Done' },
];

const statusKey = computed(() => statusOptions.find((o) => o.value === message.status)?.key ?? 'open');
const statusLabel = computed(() => statusOptions.find((o) => o.value === message.status)?.label ?? 'Open');

const statusPillRef = ref<HTMLElement>();
const statusMenuOpen = ref(false);

const pickStatus = (status: ChatTaskStatus) => {
  statusMenuOpen.value = false;
  useChat().setStatus(message.id, status);
};

// Deadline display ----------------------------------------------------------
// Deadline is a calendar day, not a timestamp — parse as date-only so the
// displayed day doesn't shift across timezones. Backend may serialise as
// "YYYY-MM-DD" or "YYYY-MM-DDT00:00:00"; slice picks the date portion.
const deadlineDate = computed(() => (message.deadline ? parseDateOnlyString(message.deadline.slice(0, 10)) : null));

const deadlineLabel = computed(() => (deadlineDate.value ? format(deadlineDate.value, 'd MMM') : ''));

// Urgency drives the chip colour. Only meaningful for non-Done tasks.
const deadlineUrgency = computed<'past' | 'soon' | 'normal' | 'done'>(() => {
  if (!deadlineDate.value) return 'normal';
  if (message.status === ChatTaskStatus.Done) return 'done';
  const days = differenceInCalendarDays(deadlineDate.value, new Date());
  if (days < 0) return 'past';
  if (days < 3) return 'soon';
  return 'normal';
});

// Edit mode -----------------------------------------------------------------
const editing = ref(false);
const editBody = ref('');
const editDeadline = ref<Date | null>(null);
const editDeadlineButtonRef = ref<HTMLElement>();
const editDeadlinePickerOpen = ref(false);
const editDeadlineLabel = computed(() => (editDeadline.value ? format(editDeadline.value, 'd MMM') : ''));

const canSaveEdit = computed(() => editBody.value.trim().length > 0);

const startEdit = () => {
  editBody.value = message.body;
  editDeadline.value = deadlineDate.value;
  editing.value = true;
};
const cancelEdit = () => {
  editing.value = false;
};
const onEditDeadlinePicked = (value: Date) => {
  editDeadline.value = value;
  editDeadlinePickerOpen.value = false;
};
const saveEdit = async () => {
  if (!canSaveEdit.value) return;
  await useChat().edit(message.id, {
    body: editBody.value.trim(),
    deadline: editDeadline.value ? dateToDateOnlyString(editDeadline.value) : undefined,
  });
  editing.value = false;
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.chat-item {
  display: grid;
  grid-template-columns: 4.8rem 1fr;
  gap: 1.2rem;
  padding: 1.1rem 2.4rem;
  position: relative;

  &--continuation {
    padding-top: 0.2rem;
    padding-bottom: 0.2rem;

    .chat-item__edit-btn {
      top: 0.1rem;
    }
  }

  // A row that names you is lifted out of the run and tinted.
  &--mentions-me {
    background: $c-accent-wash;
  }

  &:hover {
    .chat-item__gutter-time {
      opacity: 1;
    }

    // It floats over the body rather than sitting in the head, so it needs a
    // ground of its own to stay legible over a long line.
    .chat-item__edit-btn {
      opacity: 1;
      background: $c-surface;

      &:hover {
        background: $c-surface-muted;
      }
    }
  }

  // ── Gutter ───────────────────────────────────────────────────────────────
  &__gutter {
    position: relative;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-top: 0.2rem;
  }

  &__gutter-time {
    opacity: 0;
    font-size: 1.1rem;
    line-height: 2rem;
    color: rgba($c-ink, 0.5);
    transition: opacity $transition-fast ease;
  }

  &__avatar {
    position: relative;
    display: flex;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    transition: opacity 0.1s ease;

    &:hover {
      opacity: 0.7;
    }

    // Your own row and the system row have nothing to tag.
    &--static,
    &--system {
      cursor: default;

      &:hover {
        opacity: 1;
      }
    }

    // Size only — the round shape is user-avatar's own, and the chat should
    // show people exactly the way the rest of the app does.
    :deep(.user-avatar),
    :deep(.user-avatar__fallback) {
      width: 3.6rem;
      min-width: 3.6rem;
      height: 3.6rem;
      font-family: 'Montserrat', sans-serif;
      font-size: 1.2rem;
      font-weight: 600;
    }

    // Photos are often near-white (a logo on white), which would otherwise
    // dissolve into the feed. An outset ring — the avatar clips its own
    // children, so an inset one would be hidden behind the image.
    :deep(.user-avatar) {
      box-shadow: 0 0 0 1px $c-hairline-strong;
    }
  }

  &__system-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.6rem;
    height: 3.6rem;
    min-width: 3.6rem;
    border-radius: 50%;
    background: $c-surface;
    border: 1px solid $c-hairline-strong;
    box-sizing: border-box;

    :deep(svg) {
      width: 2rem;
      height: 2rem;
    }
  }

  &__unread-dot {
    position: absolute;
    inset: -0.1rem -0.3rem auto auto;
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 50%;
    background: $c-accent;
    border: 2px solid $c-surface;
  }

  // ── Content ──────────────────────────────────────────────────────────────
  &__content {
    min-width: 0;
  }

  &__head {
    display: flex;
    align-items: baseline;
    gap: 0.8rem;
    margin-bottom: 0.2rem;
  }

  &__author {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: $c-ink;
  }

  &__time {
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
    white-space: nowrap;
  }

  &__hint {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
    white-space: nowrap;

    &:hover {
      color: $c-ink;
      text-decoration: underline;
    }
  }

  &__edit-btn {
    position: absolute;
    top: 0.7rem;
    right: 2.4rem; // level with the row's own padding
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0;
    border: none;
    border-radius: $br-medium-heavy;
    background: none;
    cursor: pointer;
    opacity: 0;
    transition:
      opacity $transition-fast ease,
      background $transition-fast ease;

    :deep(svg) {
      width: 1.2rem;
      height: 1.2rem;
      fill: $c-ink-soft;
    }
  }

  &__body {
    font-size: 1.5rem;
    line-height: 2.3rem;
    color: $c-ink-body;
    white-space: pre-wrap;
    word-break: break-word;
    text-wrap: pretty;
  }

  &--done &__body {
    text-decoration: line-through;
    color: rgba($c-ink, 0.42);
  }

  &__mention {
    padding: 0.2rem 0.6rem;
    border-radius: $br-medium;
    background: rgba($c-blue, 0.1);
    color: $c-on-info-soft;
    font-weight: 700;
    cursor: pointer;

    &--me {
      background: $c-accent-strong;
      color: $c-on-accent-soft;
    }
  }

  &__role-mention {
    padding: 0.2rem 0.6rem;
    border-radius: $br-medium;
    background: rgba($c-blue, 0.1);
    color: $c-on-info-soft;
    font-weight: 700;
  }

  // ── Page-context chip ────────────────────────────────────────────────────
  &__context {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    margin-top: 0.6rem;
    padding: 0.4rem 1.1rem 0.4rem 0.9rem;
    border-radius: $br-pill;
    background: $c-surface-muted;
    font-size: 1.1rem;
    color: $c-ink-soft;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition:
      background $transition-fast ease,
      color $transition-fast ease;

    &:hover {
      background: $c-bg-gray-darkest;
      color: $c-ink;
    }

    &--on-card {
      margin-top: 0;
      background: rgba($c-ink, 0.05);
    }
  }

  &__context-icon {
    display: flex;
    flex: none;

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: currentColor;
    }
  }

  // ── Task card ────────────────────────────────────────────────────────────
  &__task {
    margin-top: 0.2rem;
    padding: 1.6rem 1.8rem 1.5rem;
    border-radius: $br-very-heavy;
    background: $c-accent-tint;

    &--done {
      background: $c-surface-muted;
    }
  }

  &__task-head {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  &__task-marker {
    margin-left: auto;
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $c-ink-soft;
  }

  &__task-foot {
    display: flex;
    align-items: center;
    margin-top: 1.2rem;
  }

  // ── Status pill ──────────────────────────────────────────────────────────
  &__status-wrap {
    display: inline-flex;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    height: 3rem;
    padding: 0 1.2rem 0 1.1rem;
    border: none;
    border-radius: $br-pill;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;

    &--open {
      background: $c-accent-soft;
      color: $c-on-accent-soft;

      .chat-item__status-dot {
        background: $c-orange;
      }
    }

    &--ongoing {
      background: rgba($c-blue, 0.12);
      color: $c-on-info-soft;

      .chat-item__status-dot {
        background: $c-blue;
      }
    }

    &--done {
      background: rgba($c-green, 0.16);
      color: $c-on-success-soft;

      .chat-item__status-dot {
        background: $c-green;
      }
    }
  }

  &__status-dot {
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 50%;
    flex: none;

    &--open {
      background: $c-orange;
    }
    &--ongoing {
      background: $c-blue;
    }
    &--done {
      background: $c-green;
    }
  }

  &__status-caret {
    display: flex;
    opacity: 0.4;

    :deep(svg) {
      width: 0.8rem;
      height: 0.8rem;
      fill: currentColor;
    }
  }

  &__status-menu {
    width: 17.6rem;
    margin: 0;
    padding: 0.6rem;
    list-style: none;
    border-radius: $br-very-heavy;
    background: $c-surface;
    box-shadow: $shadow-lifted;
  }

  &__status-option {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    height: 3.4rem;
    padding: 0 1rem;
    border: none;
    border-radius: $br-heavy;
    background: none;
    cursor: pointer;
    font-size: 1.3rem;
    color: $c-ink;
    text-align: left;

    &:hover {
      background: $c-surface-muted;
    }

    &--current {
      background: $c-canvas;
    }
  }

  &__status-check {
    margin-left: auto;
    color: $c-ink-soft;
  }

  &__status-meta {
    margin-top: 0.6rem;
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
  }

  // ── Deadline ─────────────────────────────────────────────────────────────
  &__deadline {
    font-size: 1.2rem;
    font-weight: 700;
    color: rgba($c-ink, 0.55);

    &--soon {
      color: $c-on-warning-soft;
    }

    &--past {
      color: $c-error;
    }

    &--done {
      color: rgba($c-ink, 0.5);
    }
  }

  &__deadline-btn {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1.2rem;
    font-weight: 700;
    color: $c-ink-soft;

    &:hover {
      color: $c-ink;
    }
  }

  &__deadline-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.3rem 0.6rem 0.3rem 1rem;
    border-radius: $br-pill;
    background: $c-accent-soft;
    font-size: 1.2rem;
    font-weight: 700;
    color: $c-on-accent-soft;
  }

  &__remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.8rem;
    height: 1.8rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;

    &:hover {
      background: rgba($c-on-accent-soft, 0.15);
    }

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      fill: currentColor;
    }
  }

  &__deadline-popper {
    border-radius: $br-very-heavy;
    background: $c-surface;
    box-shadow: $shadow-lifted;
    overflow: hidden;
  }

  // ── Inline edit ──────────────────────────────────────────────────────────
  &__edit-input {
    width: 100%;
  }

  &__edit-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.8rem;
    margin-top: 0.8rem;
  }

  &__edit-cancel,
  &__edit-save {
    height: 2.8rem;
    padding: 0 1.4rem;
    border: none;
    border-radius: $br-pill;
    cursor: pointer;
    font-size: 1.2rem;
    font-weight: 600;
  }

  &__edit-cancel {
    background: $c-surface-muted;
    color: $c-ink-soft;

    &:hover {
      background: $c-bg-gray-darkest;
    }
  }

  &__edit-save {
    background: $c-accent;
    color: $c-on-accent;

    &:hover {
      background: $c-accent-deep;
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
}
</style>
